import { mediaQueryLarge, isMobileBreakpoint } from "@theme/utilities";

// Accordion
class AccordionCustom extends HTMLElement {
  /** @returns {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector("details");
    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error("Details element not found");
    }
    return details;
  }

  /** @returns {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector("summary");
    if (!(summary instanceof HTMLElement)) {
      throw new Error("Summary element not found");
    }
    return summary;
  }

  /** @returns {boolean} */
  get #disableOnMobile() {
    return this.dataset.disableOnMobile === "true";
  }

  /** @returns {boolean} */
  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === "true";
  }

  /** @returns {boolean} */
  get #closeWithEscape() {
    return this.dataset.closeWithEscape === "true";
  }

  /** @type {AbortController} */
  #controller = new AbortController();

  connectedCallback() {
    const accordionDetailsContent = this.querySelector(".details-content");
    const emptyTextBlocks = this.querySelectorAll(".is-empty");
    const totalTextBlocks = accordionDetailsContent
      ? accordionDetailsContent.querySelectorAll(".text-block").length
      : 0;
    const shouldRemoveAccordionRow =
      emptyTextBlocks.length > 0 && totalTextBlocks === emptyTextBlocks.length;

    if (shouldRemoveAccordionRow) {
      this.remove();
      return;
    }

    const { signal } = this.#controller;

    // Initialize Schema Generation
    this.#initFAQSchema();

    this.#setDefaultOpenState();

    this.addEventListener("keydown", this.#handleKeyDown, { signal });
    this.summary.addEventListener("click", this.handleClick, { signal });
    this.details.addEventListener("toggle", this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener("change", this.#handleMediaQueryChange, {
      signal,
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #initFAQSchema() {
    if (window.faqSchemaTimeout) clearTimeout(window.faqSchemaTimeout);

    window.faqSchemaTimeout = setTimeout(() => {
      this.#generateGlobalSchema();
    }, 200);
  }

  /**
   * Scrapes, cleans, and injects FAQPage JSON-LD.
   */
  #generateGlobalSchema() {
    const allAccordions = document.querySelectorAll("accordion-custom");
    const faqItems = [];

    allAccordions.forEach((acc) => {
      // 1. CLEAN THE QUESTION (Plain text only)
      const questionEl = acc.querySelector("summary .text-block");
      const searchText = "Product Details";
      const questionText = questionEl?.textContent || "";

      if (
        questionText
          .toLocaleLowerCase()
          .includes(searchText.toLocaleLowerCase())
      ) {
        return;
      }

      const questionTextClean = questionEl
        ? questionEl.innerText.replace(/\s+/g, " ").trim()
        : "";

      // 2. CLEAN THE ANSWER
      const contentEl = acc.querySelector(".details-content");
      let answerHtml = "";

      if (contentEl && questionTextClean) {
        const clone = contentEl.cloneNode(true);

        // Remove UI/Noise elements
        clone
          .querySelectorAll(
            "script, style, button, svg, .icon-caret, .icon-plus",
          )
          .forEach((el) => el.remove());

        // UNWRAP LAYOUT TAGS (remove <div> but keep the text inside it)
        const tagsToUnwrap = [
          "DIV",
          "SECTION",
          "SPAN",
          "HEADER",
          "FOOTER",
          "ARTICLE",
          "RTE-FORMATTER",
          "PRODUCT-MODEL-ATTRIBUTES",
          "ILN39914",
        ];
        const elements = clone.querySelectorAll("*");

        // Loop backwards so index doesn't shift when we remove elements
        for (let i = elements.length - 1; i >= 0; i--) {
          const el = elements[i];

          if (tagsToUnwrap.includes(el.tagName)) {
            while (el.firstChild) {
              el.parentNode.insertBefore(el.firstChild, el);
            }
            el.parentNode.removeChild(el);
          } else {
            // For allowed tags (p, a, b, strong), nuke all attributes (classes/ids)
            while (el.attributes.length > 0) {
              el.removeAttribute(el.attributes[0].name);
            }
          }
        }

        answerHtml = clone.innerHTML
          .replaceAll(/\s+/g, " ")
          .replaceAll(/(<([^>]+)>)/gi, " ")
          .replaceAll(/<\/p>/g, "")
          .trim();

        if (answerHtml) {
          faqItems.push({
            "@type": "Question",
            name: questionTextClean,
            acceptedAnswer: {
              "@type": "Answer",
              text: answerHtml,
            },
          });
        }
      }
    });

    if (faqItems.length === 0) return;

    // Use current page title + FAQ suffix
    const pageName = `${document.title} | FAQs`;

    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      name: pageName,
      mainEntity: faqItems,
    };

    let scriptTag = document.getElementById("faq-structured-data");
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = "faq-structured-data";
      scriptTag.type = "application/ld+json";
      document.head.appendChild(scriptTag);
    }

    scriptTag.textContent = JSON.stringify(schema);
  }

  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if (
      (isMobile && this.#disableOnMobile) ||
      (isDesktop && this.#disableOnDesktop)
    ) {
      event.preventDefault();
    }
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    this.details.open =
      (isMobile && this.hasAttribute("open-by-default-on-mobile")) ||
      (!isMobile && this.hasAttribute("open-by-default-on-desktop"));
  }

  #handleKeyDown(event) {
    if (event.key === "Escape" && this.#closeWithEscape) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }

  #handleToggle = () => {
    if (!this.details.open) return;
    if (this.closest("facets-form-component")) return;

    const container = this.closest(".accordion");
    const allowMultipleRowsOpen =
      container?.dataset?.allowMultipleRowsOpen === "true";

    if (allowMultipleRowsOpen) return;

    const parentElement = this.parentElement;
    const accordionsInParent =
      parentElement?.querySelectorAll("accordion-custom");

    accordionsInParent?.forEach((accordion) => {
      if (accordion !== this) {
        const otherDetails = accordion.querySelector("details");
        if (otherDetails instanceof HTMLDetailsElement && otherDetails.open) {
          otherDetails.open = false;
        }
      }
    });
  };
}

if (!customElements.get("accordion-custom")) {
  customElements.define("accordion-custom", AccordionCustom);
}
